import type { TFunction } from 'i18next'

import { CommonPaths } from '@utils/path-common'
import { TransactionType } from '@utils/transaction.utils'

import { TransactionStatus } from '@model/TransactionStatus'
import { WalletTypeEnum, type CurrencyType } from '@model/WalletDto'

import type { FormatMoney } from '@hooks/useFormatNumber'

import type { WebSocketMessage } from '../core/types'
import type { WebSocketTransactionEvent, WebSocketTransactionSnackbar } from './types'

type JsonRecord = Record<string, unknown>

export enum TransactionEvent {
  Created = 'transaction_created_event',
  Pending = 'transaction_pending_event',
  Changed = 'transaction_changed_event',
  Reversed = 'transaction_reversed_event',
  Completed = 'transaction_completed_event',
  Cancelled = 'transaction_cancelled_event',
  Declined = 'transaction_declined_event',
  Failed = 'transaction_failed_event',
}

const asRecord = (value: unknown): JsonRecord | undefined =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : undefined

const pick = (record: JsonRecord | undefined, ...keys: string[]): unknown => {
  if (!record) return undefined

  return keys.map((key) => record[key]).find((value) => value !== undefined && value !== null)
}

const readString = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value.trim() || undefined

  return typeof value === 'number' ? String(value) : undefined
}

const readNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return undefined
}

const firstRecord = (value: unknown): JsonRecord | undefined =>
  Array.isArray(value) ? value.map(asRecord).find(Boolean) : undefined

const recordList = (value: unknown): JsonRecord[] =>
  Array.isArray(value)
    ? value.map(asRecord).filter((record): record is JsonRecord => record !== undefined)
    : []

const normalize = (value?: string): string => (value ?? '').trim().toLowerCase()

const TRANSACTION_TYPES = new Set<TransactionType>(
  Object.values(TransactionType).filter(
    (value): value is TransactionType => typeof value === 'number'
  )
)

const isKnownTransactionType = (value: number): value is TransactionType =>
  TRANSACTION_TYPES.has(value as TransactionType)

const WALLET_TYPES = new Set<WalletTypeEnum>(
  Object.values(WalletTypeEnum).filter(
    (value): value is WalletTypeEnum => typeof value === 'number'
  )
)

const isKnownWalletType = (value: number): value is WalletTypeEnum =>
  WALLET_TYPES.has(value as WalletTypeEnum)

const TRANSACTION_STATUSES = new Set<TransactionStatus>(
  Object.values(TransactionStatus).filter(
    (value): value is TransactionStatus => typeof value === 'number'
  )
)

const isKnownTransactionStatus = (value: number): value is TransactionStatus =>
  TRANSACTION_STATUSES.has(value as TransactionStatus)

const getEventType = (message: WebSocketMessage): TransactionEvent | undefined => {
  const eventType = normalize(readString(pick(message, 'type')))

  return Object.values(TransactionEvent).includes(eventType as TransactionEvent)
    ? (eventType as TransactionEvent)
    : undefined
}

const getTransactionType = (data: JsonRecord): TransactionType | undefined => {
  const transactionType = asRecord(pick(data, 'TransactionType'))
  const transactionTypeId = readNumber(pick(transactionType, 'Id'))

  return transactionTypeId !== undefined && isKnownTransactionType(transactionTypeId)
    ? transactionTypeId
    : undefined
}

type EventCurrency = CurrencyType | string | undefined

const isWalletTransaction = (data: JsonRecord): boolean => {
  const transactionType = getTransactionType(data)
  return (
    transactionType === TransactionType.WalletDeposit ||
    transactionType === TransactionType.WalletWithdrawal ||
    transactionType === TransactionType.Bonus
  )
}

const getCurrency = (
  data: JsonRecord,
  walletTransfer?: JsonRecord,
  tradingAccountTransfer?: JsonRecord
): EventCurrency => {
  const wallet = asRecord(pick(walletTransfer, 'Wallet'))
  const tradingAccount = asRecord(pick(tradingAccountTransfer, 'TradingAccount'))
  const currency =
    asRecord(pick(data, 'Currency')) ??
    asRecord(pick(walletTransfer, 'Currency')) ??
    asRecord(pick(wallet, 'Currency')) ??
    asRecord(pick(tradingAccountTransfer, 'Currency')) ??
    asRecord(pick(tradingAccount, 'Currency'))

  return (readString(pick(data, 'CurrencyId')) ??
    readString(pick(walletTransfer, 'CurrencyId')) ??
    readString(pick(wallet, 'CurrencyId')) ??
    readString(pick(tradingAccountTransfer, 'CurrencyId')) ??
    readString(pick(tradingAccount, 'CurrencyId')) ??
    readString(pick(currency, 'Id', 'Name'))) as EventCurrency
}

const getTargetId = (
  data: JsonRecord,
  walletTransfer?: JsonRecord,
  tradingAccountTransfer?: JsonRecord
): string | undefined => {
  if (isWalletTransaction(data)) {
    return readString(pick(asRecord(pick(walletTransfer, 'Wallet')), 'Name'))
  } else {
    return readString(pick(asRecord(pick(tradingAccountTransfer, 'TradingAccount')), 'Name'))
  }
}

const getWalletType = (walletTransfer: JsonRecord): WalletTypeEnum | undefined => {
  const wallet = asRecord(pick(walletTransfer, 'Wallet', 'wallet'))
  const walletType =
    asRecord(pick(walletTransfer, 'WalletType', 'walletType')) ??
    asRecord(pick(wallet, 'WalletType', 'walletType'))
  const walletTypeId = readNumber(pick(walletType, 'Id', 'id'))

  return walletTypeId !== undefined && isKnownWalletType(walletTypeId) ? walletTypeId : undefined
}

const getWalletTypes = (walletTransfers: JsonRecord[]): WalletTypeEnum[] => [
  ...new Set(
    walletTransfers
      .map(getWalletType)
      .filter((value): value is WalletTypeEnum => value !== undefined)
  ),
]

export const isTransactionWebSocketMessage = (message: WebSocketMessage): boolean =>
  getEventType(message) !== undefined

const getCreatedEventStatus = (
  data: JsonRecord,
  eventType: TransactionEvent
): TransactionStatus | undefined => {
  if (eventType !== TransactionEvent.Created) return undefined

  const transactionState = asRecord(pick(data, 'TransactionState'))
  const status = readNumber(pick(transactionState, 'Id'))

  return status !== undefined && isKnownTransactionStatus(status) ? status : undefined
}

export const normalizeTransactionWebSocketEvent = (
  message: WebSocketMessage
): WebSocketTransactionEvent | null => {
  const eventType = getEventType(message)
  if (!eventType) {
    return null
  }

  const data = asRecord(pick(message, 'Data')) ?? {}
  const transaction = asRecord(pick(data, 'Transaction'))
  const walletTransfers = recordList(pick(data, 'WalletTransferDetails'))
  const walletTransfer = walletTransfers[0]
  const tradingAccountTransfer = firstRecord(pick(data, 'TradingAccountTransferDetails'))

  const transactionTypeId = getTransactionType(data)
  const amount =
    readNumber(pick(tradingAccountTransfer, 'Amount')) ?? readNumber(pick(walletTransfer, 'Amount'))
  const currency = getCurrency(data, walletTransfer, tradingAccountTransfer)
  const walletBalance = readNumber(pick(walletTransfer, 'WalletBalance'))
  const transactionId = readString(pick(transaction, 'Id'))
  const walletId = readString(pick(asRecord(pick(walletTransfer, 'Wallet')), 'Id'))
  const targetId = getTargetId(data, walletTransfer, tradingAccountTransfer)
  const status = getCreatedEventStatus(data, eventType)

  return {
    type: eventType,
    transactionId,
    walletId,
    kind: transactionTypeId,
    amount,
    currency,
    walletBalance,
    balanceCurrency: currency,
    walletTypes: getWalletTypes(walletTransfers),
    targetId,
    ...(status !== undefined ? { status } : {}),
  }
}

const formatAmount = (
  formatMoney: FormatMoney,
  amount?: number,
  currency?: CurrencyType | string
): string | null => (amount === undefined ? null : formatMoney(amount, currency))

const buildCompletedMessage = (
  event: WebSocketTransactionEvent,
  formatMoney: FormatMoney,
  t: TFunction
): string | undefined => {
  const amount = formatAmount(formatMoney, event.amount, event.currency)
  const walletBalance = formatAmount(formatMoney, event.walletBalance, event.balanceCurrency)
  const currency = event.currency

  switch (event.kind) {
    case TransactionType.WalletDeposit:
      return t('Transactions.Snackbar.WalletDepositCompleted', {
        amount,
        balance: walletBalance,
        currency,
      })
    case TransactionType.WalletWithdrawal:
      return t('Transactions.Snackbar.WalletWithdrawalCompleted', { amount })
  }
}

const buildFailedMessage = (
  event: WebSocketTransactionEvent,
  formatMoney: FormatMoney,
  t: TFunction
): string | undefined => {
  const amount = formatAmount(formatMoney, event.amount, event.currency)

  switch (event.kind) {
    case TransactionType.WalletDeposit:
      return t('Transactions.Snackbar.DepositFailed', { amount })
    case TransactionType.WalletWithdrawal:
      return t('Transactions.Snackbar.WithdrawFailed', { amount })
  }
}

const ERROR_SNACKBAR_EVENTS = new Set<TransactionEvent>([
  TransactionEvent.Declined,
  TransactionEvent.Failed,
])

const ERROR_SNACKBAR_STATUSES = new Set<TransactionStatus>([
  TransactionStatus.Declined,
  TransactionStatus.Failed,
])

const isCompletedEvent = (event: WebSocketTransactionEvent): boolean =>
  event.type === TransactionEvent.Completed ||
  (event.type === TransactionEvent.Created && event.status === TransactionStatus.Completed)

const isErrorEvent = (event: WebSocketTransactionEvent): boolean =>
  ERROR_SNACKBAR_EVENTS.has(event.type) ||
  (event.type === TransactionEvent.Created &&
    event.status !== undefined &&
    ERROR_SNACKBAR_STATUSES.has(event.status))

const getTransactionHistoryPath = (event: WebSocketTransactionEvent): CommonPaths => {
  switch (event.walletTypes?.[0]) {
    case WalletTypeEnum.PAYMENT_AGENT:
      return CommonPaths.PaymentAgentTransactionHistory
    case WalletTypeEnum.IB:
      return CommonPaths.IntroducingBrokerTransactionHistory
    default:
      return CommonPaths.TradersRoomTransactionHistory
  }
}

const getDepositPath = (event: WebSocketTransactionEvent): CommonPaths => {
  switch (event.walletTypes?.[0]) {
    case WalletTypeEnum.PAYMENT_AGENT:
      return CommonPaths.PaymentAgentDeposit
    default:
      return CommonPaths.Deposit
  }
}

export const buildTransactionSnackbar = (
  event: WebSocketTransactionEvent,
  formatMoney: FormatMoney,
  canAccessTrading: boolean,
  t: TFunction
): WebSocketTransactionSnackbar | null => {
  if (isCompletedEvent(event)) {
    if (event.kind === TransactionType.WalletDeposit) {
      return {
        type: 'success',
        message: t('Transactions.Snackbar.DepositSuccessfulTitle'),
        subTitle: buildCompletedMessage(event, formatMoney, t),
        action: canAccessTrading
          ? { label: t('Trading Account.Start trading'), path: CommonPaths.Markets }
          : undefined,
      }
    }

    if (event.kind === TransactionType.WalletWithdrawal) {
      return {
        type: 'success',
        message: t('Transactions.Snackbar.WithdrawalSuccessfulTitle'),
        subTitle: buildCompletedMessage(event, formatMoney, t),
        action: {
          label: t('Trading Account.View History'),
          path: getTransactionHistoryPath(event),
        },
      }
    }
  }

  if (isErrorEvent(event)) {
    if (event.kind === TransactionType.WalletDeposit) {
      return {
        type: 'error',
        message: t('Transactions.Snackbar.DepositFailedTitle'),
        subTitle: buildFailedMessage(event, formatMoney, t),
        action: { label: t('Try again'), path: getDepositPath(event), isFundingEntry: true },
      }
    }

    if (event.kind === TransactionType.WalletWithdrawal) {
      return {
        type: 'error',
        message: t('Transactions.Snackbar.WithdrawFailedTitle'),
        subTitle: buildFailedMessage(event, formatMoney, t),
        action: {
          label: t('Trading Account.View History'),
          path: getTransactionHistoryPath(event),
        },
      }
    }
  }

  return null
}
